import { useEffect, useMemo, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import {
  useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlans as useGetMedicalPlans,
} from 'utils/web_api'

import { useGetDentalPlans } from './useGetDentalPlans'
import { useGetVisionPlans } from './useGetVisionPlans'
import { type MedicalPlanDisplayItem } from '../../member_onboarding_v2/medicalPlanTypes'
import { type DentalPlan, type VisionPlan } from '../../member_onboarding_v2/types'

/**
 * Generic helper to manage ancillary plan selection (dental/vision)
 */
function useAncillaryPlanSelection<T extends { plan_code: string; premium: { subunits: number } }> ({
  data,
  isLoading,
  error,
  planCodeInMemberCart,
  plansEnabled,
}: {
  data: { data: T[] } | undefined
  isLoading: boolean
  error: unknown
  planCodeInMemberCart: string | null
  plansEnabled: boolean
}) {
  // Sort plans by premium (lowest first)
  const sortedPlans = useMemo(() => {
    const plans = data?.data || []
    return [...plans].sort((a, b) => a.premium.subunits - b.premium.subunits)
  }, [data])

  // State for selected plan
  const [selectedPlan, setSelectedPlan] = useState<T | null>(null)

  // Get initial plan (from cart or lowest premium)
  const initialPlan = useMemo(() => {
    if (!plansEnabled || !sortedPlans || sortedPlans.length === 0) return null
    if (!planCodeInMemberCart) return sortedPlans[0]
    return sortedPlans.find((plan) => plan.plan_code === planCodeInMemberCart) || sortedPlans[0]
  }, [sortedPlans, planCodeInMemberCart, plansEnabled])

  // Set initial plan if not already selected
  useEffect(() => {
    if (plansEnabled && !selectedPlan && initialPlan) {
      setSelectedPlan(initialPlan)
    }
  }, [selectedPlan, initialPlan, plansEnabled])

  return {
    sortedPlans,
    isLoading,
    error: error as GrapeAPIErrorType | null,
    selectedPlan,
    setSelectedPlan,
  }
}

interface UseSmartBudgetPlansParams {
  enrollmentEventId: string
  dentalPlanCodeInMemberCart: string | null
  visionPlanCodeInMemberCart: string | null
  dentalPlansEnabled: boolean
  visionPlansEnabled: boolean
}

export function useSmartBudgetPlans ({
  enrollmentEventId,
  dentalPlanCodeInMemberCart,
  visionPlanCodeInMemberCart,
  dentalPlansEnabled,
  visionPlansEnabled,
}: UseSmartBudgetPlansParams) {
  // Fetch medical plans
  const {
    data: medicalPlanData,
    isLoading: isLoadingMedicalPlans,
    error: medicalPlansError,
  } = useGetMedicalPlans(
    enrollmentEventId,
    {
      expected_plan_utilization: undefined,
      oop_budget: undefined,
      'sort[]': ['deductible'],
      'metal_tier[]': undefined,
      'plan_type[]': undefined,
      'carrier_name[]': undefined,
      'provider_npis[]': undefined,
      generate_recommendations: false,
      'page[number]': 1,
      'page[size]': 1000,
    },
    {
      query: {
        retry: false,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        queryKey: [
          'enrollmentEvent',
          enrollmentEventId,
          'smart_budget',
          'medical_plans',
        ],
      },
    }
  )

  // Fetch dental plans (only if enabled)
  const dentalPlanQuery = useGetDentalPlans({
    enrollmentEventId,
    enabled: dentalPlansEnabled,
  })

  // Fetch vision plans (only if enabled)
  const visionPlanQuery = useGetVisionPlans({
    enrollmentEventId,
    enabled: visionPlansEnabled,
  })

  // Extract and type medical plans
  const medicalPlans = useMemo(
    () => (medicalPlanData?.data || []) as MedicalPlanDisplayItem[],
    [medicalPlanData]
  )

  // State for selected medical plan
  const [medicalPlan, setMedicalPlan] = useState<MedicalPlanDisplayItem | null>(null)

  // Use helper for dental plans
  const {
    sortedPlans: dentalPlans,
    isLoading: isLoadingDentalPlans,
    error: dentalPlansError,
    selectedPlan: dentalPlan,
    setSelectedPlan: setDentalPlan,
  } = useAncillaryPlanSelection<DentalPlan>({
    data: dentalPlanQuery.data,
    isLoading: dentalPlanQuery.isLoading,
    error: dentalPlanQuery.error,
    planCodeInMemberCart: dentalPlanCodeInMemberCart,
    plansEnabled: dentalPlansEnabled,
  })

  // Use helper for vision plans
  const {
    sortedPlans: visionPlans,
    isLoading: isLoadingVisionPlans,
    error: visionPlansError,
    selectedPlan: visionPlan,
    setSelectedPlan: setVisionPlan,
  } = useAncillaryPlanSelection<VisionPlan>({
    data: visionPlanQuery.data,
    isLoading: visionPlanQuery.isLoading,
    error: visionPlanQuery.error,
    planCodeInMemberCart: visionPlanCodeInMemberCart,
    plansEnabled: visionPlansEnabled,
  })

  return {
    // Medical plans
    medicalPlans,
    isLoadingMedicalPlans,
    medicalPlansError: medicalPlansError as GrapeAPIErrorType | null,
    medicalPlan,
    setMedicalPlan,
    // Dental plans
    dentalPlans,
    isLoadingDentalPlans,
    dentalPlansError,
    dentalPlan,
    setDentalPlan,
    // Vision plans
    visionPlans,
    isLoadingVisionPlans,
    visionPlansError,
    visionPlan,
    setVisionPlan,
  }
}
